import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  useCreateWhiteLabelBrandingMutation,
  useGetWhiteLabelBrandingByCompanyQuery,
  useUpdateWhiteLabelBrandingMutation,
} from "../../../redux/admin/whiteLabelApi";
import WhiteLabelWorkspaceShell from "../Components/WhiteLabelWorkspaceShell";
import {
  getApiErrorMessage,
  HEX_COLOR_REGEX,
  isSafeAssetReference,
  mapValidationErrors,
  normalizeAssetReference,
  toAssetPayload,
} from "../Utils/whiteLabelFormUtils";
import { toast } from "react-hot-toast";

const EMPTY_FORM = Object.freeze({
  companyName: "",
  productName: "",
  logo: { url: "", publicId: "" },
  loginLogo: { url: "", publicId: "" },
  favicon: { url: "", publicId: "" },
  primaryColor: "#2563EB",
  secondaryColor: "#0F172A",
  accentColor: "#14B8A6",
  supportEmail: "",
  supportPhone: "",
  websiteUrl: "",
  footerText: "",
  copyrightText: "",
});

function BrandingAssetFields({ label, value, onChange, urlError }) {
  return (
    <Stack spacing={1.25}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
        {label}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            label={`${label} URL / Path`}
            value={value.url}
            onChange={(event) =>
              onChange({
                ...value,
                url: event.target.value,
              })
            }
            error={Boolean(urlError)}
            helperText={urlError || " "}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label={`${label} Public ID`}
            value={value.publicId}
            onChange={(event) =>
              onChange({
                ...value,
                publicId: event.target.value,
              })
            }
          />
        </Grid>
      </Grid>
    </Stack>
  );
}

function normalizeBranding(branding) {
  if (!branding) return EMPTY_FORM;

  return {
    companyName: branding.companyName || "",
    productName: branding.productName || "",
    logo: normalizeAssetReference(branding.logo),
    loginLogo: normalizeAssetReference(branding.loginLogo),
    favicon: normalizeAssetReference(branding.favicon),
    primaryColor: branding.primaryColor || "#2563EB",
    secondaryColor: branding.secondaryColor || "#0F172A",
    accentColor: branding.accentColor || "#14B8A6",
    supportEmail: branding.supportEmail || "",
    supportPhone: branding.supportPhone || "",
    websiteUrl: branding.websiteUrl || "",
    footerText: branding.footerText || "",
    copyrightText: branding.copyrightText || "",
  };
}

function validateForm(form) {
  const errors = {};

  ["primaryColor", "secondaryColor", "accentColor"].forEach((field) => {
    if (form[field] && !HEX_COLOR_REGEX.test(form[field])) {
      errors[field] = "Enter a valid hex color.";
    }
  });

  ["logo", "loginLogo", "favicon"].forEach((field) => {
    if (form[field]?.url && !isSafeAssetReference(form[field].url)) {
      errors[`${field}.url`] = "Enter a valid URL or safe file reference.";
    }
  });

  if (
    form.supportEmail &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.supportEmail).trim())
  ) {
    errors.supportEmail = "Support email must be valid.";
  }

  if (form.websiteUrl) {
    try {
      new URL(form.websiteUrl);
    } catch (_) {
      errors.websiteUrl = "Website URL must be valid.";
    }
  }

  return errors;
}

function buildPayload(companyId, form) {
  return {
    companyId,
    companyName: String(form.companyName || "").trim(),
    productName: String(form.productName || "").trim(),
    logo: toAssetPayload(form.logo),
    loginLogo: toAssetPayload(form.loginLogo),
    favicon: toAssetPayload(form.favicon),
    primaryColor: String(form.primaryColor || "").trim() || undefined,
    secondaryColor: String(form.secondaryColor || "").trim() || undefined,
    accentColor: String(form.accentColor || "").trim() || undefined,
    supportEmail: String(form.supportEmail || "").trim() || undefined,
    supportPhone: String(form.supportPhone || "").trim() || undefined,
    websiteUrl: String(form.websiteUrl || "").trim() || undefined,
    footerText: String(form.footerText || "").trim() || undefined,
    copyrightText: String(form.copyrightText || "").trim() || undefined,
  };
}

export default function WhiteLabelBrandingPage() {
  const theme = useTheme();
  const [companyId, setCompanyId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const {
    data: branding,
    isFetching,
    error,
    refetch,
  } = useGetWhiteLabelBrandingByCompanyQuery(companyId, {
    skip: !companyId,
  });

  const [createBranding, { isLoading: isCreating }] =
    useCreateWhiteLabelBrandingMutation();
  const [updateBranding, { isLoading: isUpdating }] =
    useUpdateWhiteLabelBrandingMutation();

  useEffect(() => {
    if (branding) {
      setForm(normalizeBranding(branding));
      setFieldErrors({});
      setSubmitError("");
    }
  }, [branding]);

  useEffect(() => {
    if (!companyId) {
      setForm(EMPTY_FORM);
      setFieldErrors({});
      setSubmitError("");
    }
  }, [companyId]);

  const isMissingRecord = error?.status === 404;
  const errorMessage =
    error && !isMissingRecord
      ? getApiErrorMessage(error, "Failed to load branding.")
      : "";

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    setSubmitError("");
  };

  const handleSubmit = async () => {
    const nextErrors = validateForm(form);
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    try {
      const payload = buildPayload(companyId, form);
      if (isMissingRecord) {
        await createBranding(payload).unwrap();
        toast.success("Branding created successfully.");
      } else {
        await updateBranding(payload).unwrap();
        toast.success("Branding updated successfully.");
      }
      setSubmitError("");
      refetch();
    } catch (submitErrorValue) {
      const validationErrors = mapValidationErrors(submitErrorValue);
      setFieldErrors((prev) => ({ ...prev, ...validationErrors }));
      const message = getApiErrorMessage(
        submitErrorValue,
        "Unable to save branding."
      );
      setSubmitError(message);
      toast.error(message);
    }
  };

  return (
    <WhiteLabelWorkspaceShell
      title="Branding Manager"
      description="Load, create, view, and update company-specific branding from the frozen White Label backend."
      companyId={companyId}
      onCompanyChange={setCompanyId}
      error={errorMessage}
      actions={
        <Chip
          label={isMissingRecord ? "No Branding Yet" : "Backend Connected"}
          color={isMissingRecord ? "warning" : "success"}
          variant={isMissingRecord ? "outlined" : "filled"}
          sx={{ fontWeight: 800 }}
        />
      }
    >
      {!companyId ? (
        <Alert severity="info">Select a company to manage branding.</Alert>
      ) : isFetching ? (
        <Alert severity="info">Loading branding configuration...</Alert>
      ) : (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, xl: 8 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: { xs: 2, md: 2.5 },
                border: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
              }}
            >
              <Stack spacing={2}>
                {isMissingRecord ? (
                  <Alert severity="info">
                    Branding does not exist for this company yet. Saving this form
                    will create it using the existing backend API.
                  </Alert>
                ) : null}
                {submitError ? <Alert severity="error">{submitError}</Alert> : null}

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      value={form.companyName}
                      onChange={(event) =>
                        handleFieldChange("companyName", event.target.value)
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Product Name"
                      value={form.productName}
                      onChange={(event) =>
                        handleFieldChange("productName", event.target.value)
                      }
                    />
                  </Grid>
                </Grid>

                <BrandingAssetFields
                  label="Company Logo"
                  value={form.logo}
                  onChange={(value) => handleFieldChange("logo", value)}
                  urlError={fieldErrors["logo.url"]}
                />
                <BrandingAssetFields
                  label="Login Logo"
                  value={form.loginLogo}
                  onChange={(value) => handleFieldChange("loginLogo", value)}
                  urlError={fieldErrors["loginLogo.url"]}
                />
                <BrandingAssetFields
                  label="Favicon"
                  value={form.favicon}
                  onChange={(value) => handleFieldChange("favicon", value)}
                  urlError={fieldErrors["favicon.url"]}
                />

                <Grid container spacing={2}>
                  {[
                    ["primaryColor", "Primary Color"],
                    ["secondaryColor", "Secondary Color"],
                    ["accentColor", "Accent Color"],
                  ].map(([field, label]) => (
                    <Grid key={field} size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label={label}
                        value={form[field]}
                        onChange={(event) =>
                          handleFieldChange(field, event.target.value)
                        }
                        error={Boolean(fieldErrors[field])}
                        helperText={fieldErrors[field] || " "}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Support Email"
                      value={form.supportEmail}
                      onChange={(event) =>
                        handleFieldChange("supportEmail", event.target.value)
                      }
                      error={Boolean(fieldErrors.supportEmail)}
                      helperText={fieldErrors.supportEmail || " "}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Support Phone"
                      value={form.supportPhone}
                      onChange={(event) =>
                        handleFieldChange("supportPhone", event.target.value)
                      }
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  label="Website URL"
                  value={form.websiteUrl}
                  onChange={(event) =>
                    handleFieldChange("websiteUrl", event.target.value)
                  }
                  error={Boolean(fieldErrors.websiteUrl)}
                  helperText={fieldErrors.websiteUrl || " "}
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Footer Text"
                  value={form.footerText}
                  onChange={(event) =>
                    handleFieldChange("footerText", event.target.value)
                  }
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Copyright Text"
                  value={form.copyrightText}
                  onChange={(event) =>
                    handleFieldChange("copyrightText", event.target.value)
                  }
                />

                <Stack direction="row" spacing={1.25} justifyContent="flex-end">
                  <Button variant="outlined" onClick={() => setForm(normalizeBranding(branding))} disabled={isCreating || isUpdating || isMissingRecord}>
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isCreating || isUpdating}
                  >
                    {isCreating || isUpdating
                      ? "Saving..."
                      : isMissingRecord
                        ? "Create Branding"
                        : "Save Branding"}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, xl: 4 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 3,
                  background: `linear-gradient(135deg, ${form.primaryColor || "#2563EB"} 0%, ${form.accentColor || "#14B8A6"} 100%)`,
                  color: "#fff",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {form.productName || form.companyName || "Brand Preview"}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.88, mt: 0.5 }}>
                  Branding values reflect the saved backend shape for this company.
                </Typography>
              </Box>
              <Stack spacing={1.25} sx={{ p: 2.5 }}>
                <Typography sx={{ fontWeight: 800 }}>
                  Support
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.supportEmail || "No support email"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.supportPhone || "No support phone"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.websiteUrl || "No website URL"}
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    borderRadius: 2.5,
                    p: 2,
                    bgcolor: alpha(form.secondaryColor || "#0F172A", 0.08),
                    border: `1px solid ${alpha(form.secondaryColor || "#0F172A", 0.16)}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Footer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {form.footerText || "Footer text not configured."}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </WhiteLabelWorkspaceShell>
  );
}
